import {
  interestFilterFields,
  validatedPropertyInterestDataSchema,
  type InterestConfigType,
  type Location,
  type PropertyFiltersType,
  type PropertyInterestData,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { View } from 'react-native'
import FlowNavigation from '~/components/FlowNavigation'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { InterestLocations } from '~/components/interests/inputs/SelectInterestLocations'
import { InterestFilterConfigurator } from '~/components/interests/InterestFilterConfigurator'
import { Text } from '~/components/ui/text'
import { propertyFilterFormSchema } from '~/lib/propertyFormSchemas'

export function ConfigureFeedbackInterest({
  onChangeLocations,
  interestData,
  onChangeFilters,
  isForContact = false,
}: {
  interestData?: Pick<PropertyInterestData, 'filters' | 'locations'>
  onChangeLocations: Dispatch<SetStateAction<Location[]>>
  onChangeFilters: Dispatch<SetStateAction<PropertyFiltersType>>
  isForContact?: boolean
}) {
  const { t } = useTranslations('interest')
  const { recalculateCanProceed, nextPhase, previousPhase } = useFlowManager()

  const filters: PropertyFiltersType = useMemo(() => interestData?.filters ?? {}, [interestData])
  const selectedLocations: Location[] = useMemo(() => interestData?.locations ?? [], [interestData])
  const [activatedFilters, setActivatedFilters] = useState<InterestConfigType[]>([])

  const interestIsValid = useMemo(() => {
    const structureValid = validatedPropertyInterestDataSchema
      .pick({ filters: true, locations: true })
      .safeParse(interestData).success

    if (!structureValid) {
      return false
    }

    return propertyFilterFormSchema.safeParse(interestData?.filters).success
  }, [interestData])

  const renderLocationsEditor = useCallback(
    ({
      locations,
      onChangeLocations: handleLocationsChange,
    }: {
      locations: Location[]
      onChangeLocations: (locations: Location[]) => void
    }) => (
      <InterestLocations
        asset={null}
        locations={locations}
        setLocations={handleLocationsChange}
        isForContact={isForContact}
      />
    ),
    [isForContact]
  )

  useEffect(() => {
    recalculateCanProceed()
  }, [filters, selectedLocations, recalculateCanProceed])

  const allFiltersActivated =
    activatedFilters.length >= Object.keys(interestFilterFields).length + 1

  return (
    <View className="relative isolate z-10">
      <InterestFilterConfigurator
        filters={filters}
        locations={selectedLocations}
        onChangeFilters={onChangeFilters}
        onChangeLocations={onChangeLocations}
        renderLocationsEditor={renderLocationsEditor}
        context={isForContact ? 'contact' : 'own'}
        onActivatedFiltersChange={setActivatedFilters}
      />
      {!interestIsValid && (
        <View className="pb-4 pt-8">
          <Text className="text-center font-light text-2xl/extra-loose text-neutral-400">
            {t(($) => (isForContact ? $.details.tellMoreContact : $.details.tellMore))}
          </Text>
        </View>
      )}
      <FlowNavigation
        nextTestId="interest-flow-next-button"
        previousTestId="interest-flow-previous-button"
        className="web:relative web:py-0 web:sm:py-0 mt-12 h-auto px-0 py-0"
        onNext={() => nextPhase()}
        onPrevious={() => previousPhase()}
        nextDisabled={!interestIsValid}
        showBackground={false}
        nextButtonVariant={allFiltersActivated ? 'primary' : 'outline'}
      />
    </View>
  )
}
